import { useMutation } from "@apollo/client";
import { Alert, Box, Button, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { SEND_PERSONAL_MESSAGES } from "../gql/queries/cancellations";
import BulkSendProgressPanel from "./ChatAutomations/BulkSendProgressPanel";

interface Props {
  /** Who the message goes to. */
  email: string;
  name: string;
  /** The filled-in message to start from. She can edit it before sending. */
  initialMessage: string;
  /** Outreach-log tag, so the send is attributable. */
  campaign: string;
  /** Shown as a warning strip — e.g. "messaged 3 days ago". */
  warning?: string;
  onClose: () => void;
  onSent?: () => void;
}

/**
 * Review-and-send for one member.
 *
 * The bulk screens answer "message all of these people"; this answers "message
 * *her*, and let me read it first". That's the interaction the member tools have
 * and the admin didn't: the message you're about to send is on screen, editable,
 * next to the name of the person receiving it.
 *
 * Deliberately one member at a time — the whole point is seeing the actual words
 * before they go out, which a bulk box can't give you.
 */
const MemberMessagePanel: React.FC<Props> = ({
  email,
  name,
  initialMessage,
  campaign,
  warning,
  onClose,
  onSent,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [message, setMessage] = useState(initialMessage);
  const [sendId, setSendId] = useState<string | null>(null);
  const [runSend, { loading }] = useMutation(SEND_PERSONAL_MESSAGES);

  // Picking a different member (or a different angle) has to reload the draft,
  // or she'd be editing the previous member's message under a new name.
  // biome-ignore lint/correctness/useExhaustiveDependencies: changing recipient must clear the previous send even when the draft text is identical.
  useEffect(() => {
    setMessage(initialMessage);
    setSendId(null);
  }, [initialMessage, email]);

  const send = async (): Promise<void> => {
    const text = message.trim();
    if (!text) {
      enqueueSnackbar("Write a message first.", { variant: "info" });
      return;
    }
    if (!window.confirm(`Send this message to ${name}?`)) {
      return;
    }
    try {
      const { data } = await runSend({
        variables: { input: { recipients: [{ email, message: text }], campaign } },
      });
      const result = data?.chatAutomationsSendPersonalMessages;
      if (result?.sendId) {
        setSendId(result.sendId);
        enqueueSnackbar(`Queued for ${name}.`, { variant: "info" });
      } else if (result?.succeeded) {
        enqueueSnackbar(`Sent to ${name}.`, { variant: "success" });
        onSent?.();
        onClose();
      } else {
        enqueueSnackbar(`Couldn't send to ${name}.`, { variant: "warning" });
      }
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Send failed.", {
        variant: "error",
      });
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: "action.hover" }}>
      <Stack spacing={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            💬 Message {name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {email}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            ✕
          </IconButton>
        </Box>

        {sendId && <BulkSendProgressPanel sendId={sendId} onComplete={onSent} />}
        {warning && <Alert severity="warning">{warning}</Alert>}

        <TextField
          multiline
          minRows={8}
          fullWidth
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          helperText="This is exactly what she'll receive — edit it if you like."
        />

        <Box display="flex" gap={1}>
          <Button variant="contained" onClick={send} disabled={loading || Boolean(sendId)}>
            {loading ? "Sending…" : "💬 Send message"}
          </Button>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default MemberMessagePanel;
